/**
 * Cola de eventos de uso: en memoria, con copia ligera en un almacén
 * (localStorage) para no perder lo pendiente al cerrar la app. Lógica pura y
 * testeable; el envío está en `./index.ts`.
 */
import { limpiarDatos } from './privacidad';

export type DispositivoUso = {
  /** Ancho y alto lógicos de la ventana (px CSS). */
  ancho: number;
  alto: number;
  /** Puntero táctil (móvil/tableta). */
  tactil: boolean;
  /** Ventana estrecha (interfaz móvil). */
  estrecho: boolean;
  /** El sistema pide modo oscuro. */
  oscuro: boolean;
  /** Tema elegido en la app (p. ej. «auto», «light», «dark»). */
  tema: string | null;
  /** Escala de la interfaz (Ajustes → Tamaño) y zoom de respaldo. */
  escala: number;
  zoom: number;
  /** Densidad de píxeles. */
  dpr: number;
  /** Abierta como app instalada (PWA). */
  instalada: boolean;
  /** Tipo de aparato: «ios», «android» o «escritorio» (nada más). */
  plataforma?: 'ios' | 'android' | 'escritorio';
};

export type EventoUso = {
  v: 1;
  /** Id aleatorio del evento (para quitar duplicados al analizar). */
  id: string;
  /** Fecha y hora ISO. */
  ts: string;
  /** Id aleatorio de esta apertura de la app. */
  sesion: string;
  tipo: string;
  disp: DispositivoUso;
  app: { version: string | null; commit: string | null };
  /** Toques en pantalla desde el evento anterior (solo el número). */
  toques: number;
  datos: Record<string, unknown>;
};

const RE_TIPO = /^[a-z][a-z0-9_]{0,40}$/;

export type ContextoEvento = {
  sesion: string;
  disp: DispositivoUso;
  app: EventoUso['app'];
  toques: number;
  ahora?: Date;
  id?: string;
};

/** Id aleatorio corto (base 36). */
export function idAleatorio(): string {
  const bytes = new Uint8Array(9);
  try {
    crypto.getRandomValues(bytes);
  } catch {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes, b => b.toString(36).padStart(2, '0')).join('');
}

/** Crea un evento ya filtrado, o null si el tipo no es válido. */
export function crearEvento(
  tipo: string,
  datos: Record<string, unknown> | undefined,
  ctx: ContextoEvento,
): EventoUso | null {
  if (!RE_TIPO.test(tipo)) {
    return null;
  }
  return {
    v: 1,
    id: ctx.id ?? idAleatorio(),
    ts: (ctx.ahora ?? new Date()).toISOString(),
    sesion: ctx.sesion,
    tipo,
    disp: ctx.disp,
    app: ctx.app,
    toques: ctx.toques,
    datos: limpiarDatos(datos),
  };
}

/** Una línea JSON por evento (formato JSONL). */
export function aJsonl(eventos: readonly unknown[]): string {
  return eventos.map(e => JSON.stringify(e) + '\n').join('');
}

export type Almacen = {
  leer: () => string | null;
  escribir: (texto: string) => void;
  borrar: () => void;
};

export class ColaUso {
  private eventos: EventoUso[] = [];

  constructor(
    private readonly max = 1000,
    private readonly almacen?: Almacen,
  ) {}

  get tamano() {
    return this.eventos.length;
  }

  /** Añade al final; si se pasa del máximo, se pierden los más antiguos. */
  anadir(e: EventoUso) {
    this.eventos.push(e);
    if (this.eventos.length > this.max) {
      this.eventos.splice(0, this.eventos.length - this.max);
    }
  }

  /**
   * Los primeros eventos pendientes sin sacarlos de la cola (se quitan con
   * `confirmar` cuando el servidor los ha guardado). Corta por número y por
   * tamaño del JSON (fetch con keepalive admite ~64 KB).
   */
  pendientes(maxEventos = 200, maxBytes = 60_000): EventoUso[] {
    const lote: EventoUso[] = [];
    let bytes = 20;
    for (const e of this.eventos) {
      const tam = JSON.stringify(e).length + 1;
      if (
        lote.length >= maxEventos ||
        (lote.length > 0 && bytes + tam > maxBytes)
      ) {
        break;
      }
      lote.push(e);
      bytes += tam;
    }
    return lote;
  }

  /** Quita de la cola los eventos ya enviados (por id). */
  confirmar(lote: readonly EventoUso[]) {
    const ids = new Set(lote.map(e => e.id));
    this.eventos = this.eventos.filter(e => !ids.has(e.id));
  }

  todos(): readonly EventoUso[] {
    return this.eventos;
  }

  vaciar() {
    this.eventos = [];
    try {
      this.almacen?.borrar();
    } catch {
      // sin almacenamiento
    }
  }

  guardar() {
    if (!this.almacen) {
      return;
    }
    try {
      if (this.eventos.length === 0) {
        this.almacen.borrar();
      } else {
        this.almacen.escribir(JSON.stringify(this.eventos));
      }
    } catch {
      // lleno o bloqueado: se queda solo en memoria
    }
  }

  /** Recupera lo pendiente de la vez anterior (delante de lo nuevo). */
  cargar() {
    if (!this.almacen) {
      return;
    }
    try {
      const texto = this.almacen.leer();
      const guardados: unknown = texto ? JSON.parse(texto) : [];
      if (!Array.isArray(guardados)) {
        return;
      }
      const validos = guardados.filter(
        (e): e is EventoUso =>
          typeof e === 'object' &&
          e !== null &&
          typeof (e as EventoUso).id === 'string' &&
          typeof (e as EventoUso).tipo === 'string',
      );
      const ids = new Set(this.eventos.map(e => e.id));
      this.eventos = [
        ...validos.filter(e => !ids.has(e.id)),
        ...this.eventos,
      ].slice(-this.max);
    } catch {
      // copia rota: se ignora
    }
  }
}
